<script lang="ts">
  import { sanityStudioURL } from '$lib/client';
  import type { LegalLicense } from '$lib/sanity/schemas/entities/LegalLicensesPage';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { slugify } from '$lib/utils/slugify';
  import type { ContentSourceMap } from '@sanity/client';
  import { useEncodeDataAttribute, type StudioPathLike } from '@sanity/svelte-loader';
  import { ResponsiveVars } from '@twist/core';
  import { createEventDispatcher } from 'svelte';

  export let licenses: LegalLicense[];
  export let sourceMap: ContentSourceMap | undefined = undefined;
  export let path: StudioPathLike = [];

  $: encodeDataAttribute = useEncodeDataAttribute(licenses, sourceMap, sanityStudioURL);

  const dispatch = createEventDispatcher();

  function handleClick(license: LegalLicense) {
    dispatch('jurisdictionClick', { license });
  }

  const responsiveVars = new ResponsiveVars()
    .addFluid('py', toFluidVarInput(12, 36))
    .addFluid('px', toFluidVarInput(12, 70))
    .addFluid('font-size', toFluidVarInput(12, 21))
    .toCSS();
</script>

<table class={responsiveVars}>
  <thead>
    <tr>
      <th>Jurisdiction</th>
      <th>Regulatory Agency</th>
      <th>License No.</th>
    </tr>
  </thead>
  <tbody>
    {#each licenses as license, index}
      <tr>
        <td>
          <a
            href={`#${slugify(license.jurisdiction)}`}
            on:click|preventDefault={() => handleClick(license)}
          >
            {#if license.jurisdictionLabel}
              <div data-sanity={encodeDataAttribute([...path, index, 'jurisdictionLabel'])}>
                {license.jurisdictionLabel}
              </div>
            {:else}
              <div data-sanity={encodeDataAttribute([...path, index, 'jurisdiction'])}>
                {license.jurisdiction}
              </div>
            {/if}
          </a>
        </td>
        <td data-sanity={encodeDataAttribute([...path, index, 'regulatoryAgency'])}
          >{license.regulatoryAgency}</td
        >
        <td data-sanity={encodeDataAttribute([...path, index, 'licenseNumber'])}
          >{license.licenseNumber ?? ''}</td
        >
      </tr>
    {/each}
  </tbody>
</table>

<style lang="scss">
  @import 'src/scss/media';

  table {
    table-layout: fixed;
    border-collapse: collapse;
    width: 100%;

    font-family: var(--font-family-primary);

    th,
    td {
      border: 1px solid #000;
    }

    tr {
      &:first-child th {
        border-top: 0;
      }

      &:last-child td {
        border-bottom: 0;
      }

      td:first-child,
      th:first-child {
        border-left: 0;
      }

      td:last-child,
      th:last-child {
        border-right: 0;
      }
    }

    tr {
      th:nth-child(1),
      td:nth-child(1) {
        width: 30%;

        @include at('lg') {
          width: 20%;
        }
      }

      th:nth-child(2),
      td:nth-child(2) {
        width: 40%;

        @include at('lg') {
          width: 50%;
        }
      }

      th:nth-child(3),
      td:nth-child(3) {
        width: 30%;
      }
    }

    th {
      padding: var(--py) var(--px);
      text-align: left;
      font-weight: bold;

      font-size: var(--font-size);
      line-height: 100%;

      &:first-of-type {
        padding-left: 0;
      }

      &:last-of-type {
        padding-right: 0;
      }
    }

    td {
      padding: var(--py) var(--px);
      text-align: left;

      font-size: var(--font-size);
      line-height: 115%;

      &:first-of-type {
        padding-left: 0;
      }

      &:last-of-type {
        padding-right: 0;
      }

      a {
        color: #000;

        &:hover {
          color: #fff;
        }

        &:focus-visible {
          color: #fff;
          outline: none;
        }

        &:active {
          opacity: 0.6;
        }
      }
    }
  }
</style>
